import React from "react";
import { CardholderLocalState } from "../../../../pages/cardholder-basic-information/types";
import { useScrollToTop } from "../../../../pages/shared/hooks/useScrollToTop";
import AdditionalAccountApplicationFlow from "./additionalAccountApplicationFlow";
import AdditionalCardApplicationFlow from "./additionalCardApplicationFlow";
import AdditionalCardApplicationLocalStateFlow from "./additionalCardApplicationFlow/localStateFlow";
import FirstAccountApplicationFlow from "./firstAccountApplicationFlow";
import NoApplicationsPresentFlow from "./noApplicationsPresentFlow";
import resolveFlow from "./resolveFlow";
import {
  ApplicationFlows,
  FlowPosition,
  stepToChapter,
  stepToFlowPosition,
} from "./types";
import useStep from "./useStep";
import {
  BasicAccountApplicationDocument,
  CardApplicationDocument,
  SigningProcessDocument,
} from "../../../../api/commercial/generated-types";
import {
  CancelApplicationContextProvider,
  CancellationCase as ApplicationFlowCancel,
} from "../../../../context/cancelApplicationContext";
import { ProductRefType } from "../../../../context/product-context/types";

export default function AuthenticatedApplicationFlow({
  additionalCAFlowLocalState,
  basicAccountApplication,
  cardApplication,
  noValidApplications,
  signingProcess,
  triggerReload,
  hasDisabledProduct,
  productFromServer,
}: {
  additionalCAFlowLocalState: CardholderLocalState;
  basicAccountApplication: BasicAccountApplicationDocument;
  cardApplication: CardApplicationDocument;
  noValidApplications: boolean;
  signingProcess: SigningProcessDocument;
  triggerReload: () => void;
  hasDisabledProduct: boolean;
  productFromServer: ProductRefType;
}): React.JSX.Element {
  const [step, setStepAndTrack] = useStep(
    basicAccountApplication,
    cardApplication,
    noValidApplications,
  );

  useScrollToTop(step);

  const activeChapter = stepToChapter[step];

  if (!cardApplication && Boolean(additionalCAFlowLocalState)) {
    return (
      <CancelApplicationContextProvider
        cancellationCase={ApplicationFlowCancel.NOT_PERSISTED}
        applicationFlow={ApplicationFlows.ADDITIONAL_CA}
        hasDisabledProduct={hasDisabledProduct}
        productFromServer={productFromServer}
      >
        <AdditionalCardApplicationLocalStateFlow
          localState={additionalCAFlowLocalState}
          triggerReload={triggerReload}
          setStep={setStepAndTrack}
        />
      </CancelApplicationContextProvider>
    );
  }

  const flow = resolveFlow(
    basicAccountApplication,
    cardApplication,
    noValidApplications,
  );

  if (flow === ApplicationFlows.NO_APPLICATIONS_FLOW) {
    return (
      <CancelApplicationContextProvider
        cancellationCase={ApplicationFlowCancel.NOT_PERSISTED}
        applicationFlow={flow}
        hasDisabledProduct={hasDisabledProduct}
        productFromServer={productFromServer}
      >
        <NoApplicationsPresentFlow
          activeChapter={activeChapter}
          step={step}
          setStep={setStepAndTrack}
          triggerReload={triggerReload}
        />
      </CancelApplicationContextProvider>
    );
  }

  if (
    flow === ApplicationFlows.FIRST_BAA ||
    flow === ApplicationFlows.ADDITIONAL_BAA
  ) {
    const positionInFlow: FlowPosition = stepToFlowPosition[step];

    const cancellationFlow: ApplicationFlowCancel =
      positionInFlow === FlowPosition.IN_BAA
        ? ApplicationFlowCancel.BAA
        : ApplicationFlowCancel.FIRST_CA;

    if (flow === ApplicationFlows.FIRST_BAA) {
      return (
        <CancelApplicationContextProvider
          basicApplicationRef={basicAccountApplication?.applicationRef}
          cardApplicationRef={cardApplication?.applicationRef}
          currentStep={step}
          applicationFlow={flow}
          cancellationCase={cancellationFlow}
          hasDisabledProduct={hasDisabledProduct}
          productFromServer={productFromServer}
        >
          <FirstAccountApplicationFlow
            basicAccountApplication={basicAccountApplication}
            cardApplication={cardApplication}
            signingProcess={signingProcess}
            activeChapter={activeChapter}
            step={step}
            setStep={setStepAndTrack}
            triggerReload={triggerReload}
          />
        </CancelApplicationContextProvider>
      );
    } else if (flow === ApplicationFlows.ADDITIONAL_BAA) {
      if (flow === ApplicationFlows.ADDITIONAL_BAA) {
        return (
          <CancelApplicationContextProvider
            basicApplicationRef={basicAccountApplication?.applicationRef}
            cardApplicationRef={cardApplication?.applicationRef}
            currentStep={step}
            applicationFlow={flow}
            cancellationCase={cancellationFlow}
            hasDisabledProduct={hasDisabledProduct}
            productFromServer={productFromServer}
          >
            <AdditionalAccountApplicationFlow
              basicAccountApplication={basicAccountApplication}
              cardApplication={cardApplication}
              signingProcess={signingProcess}
              activeChapter={activeChapter}
              step={step}
              setStep={setStepAndTrack}
              triggerReload={triggerReload}
            />
          </CancelApplicationContextProvider>
        );
      }
    }
  }

  if (flow === ApplicationFlows.ADDITIONAL_CA) {
    return (
      <CancelApplicationContextProvider
        basicApplicationRef={cardApplication?.basicAccountApplicationRef}
        cardApplicationRef={cardApplication?.applicationRef}
        applicationFlow={ApplicationFlows.ADDITIONAL_CA}
        currentStep={step}
        cancellationCase={ApplicationFlowCancel.ADDITIONAL_CA}
        hasDisabledProduct={hasDisabledProduct}
        productFromServer={productFromServer}
      >
        <AdditionalCardApplicationFlow
          basicAccountApplication={basicAccountApplication}
          cardApplication={cardApplication}
          activeChapter={activeChapter}
          step={step}
          setStep={setStepAndTrack}
          triggerReload={triggerReload}
        />
      </CancelApplicationContextProvider>
    );
  }

  return null;
}
